import { CMSApi } from 'api';
import { t } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { CmsParser } from 'shared/components/CmsParser';
import { HelmetMetaTags } from 'shared/components/HelmetMetaTags';
import { TransactionsLoader } from 'shared/components/loader/transactionsLoader';
import { APP_URL, promotionConfig, PROMOTION_URL } from 'shared/constants/common';
import { addNotification } from '../../../shared/components/toast';
import { fetchPromotionName, PromotionTitle } from '../store/promotionDetails.action';
import CashoutPromoPolicy from './CashoutPromoPolicy';

const promotionDetailsInitial = {
	description: '',
	imageAltText: '',
	imageUrl: '',
	promotionId: '',
	title: '',
	dateTill: '',
	isTimed: false,
	primaryButton: {
		link: '',
		text: '',
	},
};

const PromotionDetails = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const params = useParams<{ promotionId: string }>();
	const [promotionDetails, setPromotionDetails] = useState<PromotionTitle>(promotionDetailsInitial);
	const [loading, setLoading] = useState<boolean>(false);
	const [promotionContent, setPromotionContent] = useState<string>('');

	const [seoConfig, setSeoConfig] = useState({
		is301: false,
		prerenderRedirectUrl: '',
		seoTitle: '',
	});

	const isCashoutPromoPage = window.location.pathname === PROMOTION_URL.cashout;

	const getPromotionDetails = useCallback(
		async (promotionId: string) => {
			try {
				setLoading(true);
				const response = await CMSApi.getPromotionDetails(promotionId);
				setLoading(false);

				if (response && response.data && response.data.data) {
					setSeoConfig({
						is301: response.data.data.redirect,
						prerenderRedirectUrl: response.data.data.redirectUrl,
						seoTitle: response.data.data.promotion.title || '',
					});

					const promotionDetails = response.data.data.promotion || {};
					const promotionContent = response.data.data.promotion.description || '';

					if (promotionDetails.dateTill) {
						const archiveThreshold = new Date(promotionDetails.dateTill);
						archiveThreshold.setDate(archiveThreshold.getDate() + promotionConfig.AVAILABLE_AFTER_EXPIRED_IN_DAYS);
						if (new Date() >= archiveThreshold) {
							addNotification('error', `${t('promotionExpired')}!`);
							history.replace(APP_URL.promotion);
						}
					}

					setPromotionDetails(promotionDetails);
					setPromotionContent(promotionContent);
					dispatch(fetchPromotionName(promotionDetails));
				}
			} catch (error) {
				setLoading(false);
				addNotification('error', `${t('promotionNotExisting')}!`);
				history.replace(APP_URL.promotion);
			}
		},
		[dispatch],
	);

	useEffect(() => {
		const fetchDetailsData = async () => {
			const promotionId = params.promotionId;
			await getPromotionDetails(promotionId);
		};
		fetchDetailsData();
	}, [getPromotionDetails, params.promotionId]);

	return (
		<>
			<HelmetMetaTags
				title={seoConfig.seoTitle && `Promocja ${seoConfig.seoTitle} | BETFAN`}
				description={t('promotionAboutBetfan')}
				is301={seoConfig.is301}
				redirectUrl={seoConfig.prerenderRedirectUrl}
			/>
			<div className="promotion-details-wrapper">
				{!loading && !isEmpty(promotionDetails) && (
					<CmsParser
						data={promotionContent}
						showTimer={promotionDetails.isTimed}
						countdownDate={promotionDetails.dateTill}
						promotionId={promotionDetails.promotionId}
						primaryButton={promotionDetails.primaryButton}
						isPromoButton={promotionDetails.isPromoButton}
						offerDefinitionId={promotionDetails.offerDefinitionId}
					/>
				)}

				{loading && <TransactionsLoader />}
			</div>
			{isCashoutPromoPage && <CashoutPromoPolicy />}
		</>
	);
};

export default PromotionDetails;
